import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  LayoutDashboard, Database, Settings, Menu, X, BarChart3, RefreshCw, Shield,
  TrendingUp, LogOut, User, Users, Building2, UserCircle, Moon, Sun, FileBarChart, Briefcase, Eye, EyeOff
} from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { cn } from "../lib/utils";
import { useMsal } from "../context/MsalContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useVisibilityOverride } from "../context/VisibilityOverrideContext";

function DataSourceStatus() {
  const [dsType, setDsType] = useState("manual");
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ""}/api/sync/status`).then(res => {
      if (res.data?.source_type === "ms365") setDsType("ms365");
    }).catch(() => {});
  }, []);
  return dsType === "ms365" ? (
    <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500" /><span className="text-xs text-emerald-600 dark:text-emerald-400">MS365 Synced</span></div>
  ) : (
    <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-amber-500" /><span className="text-xs text-amber-600 dark:text-amber-400">Manual Upload</span></div>
  );
}

const ROLE_COLORS = {
  employee: { bg: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300", label: "Employee" },
  user: { bg: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300", label: "Employee" },
  manager: { bg: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300", label: "Manager" },
  director: { bg: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300", label: "Director" },
  admin: { bg: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300", label: "Admin" },
};

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isTeamLeader, setIsTeamLeader] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated: msalAuthenticated } = useMsal();
  const { user, role, isAdmin, isDirector, isManager, logout } = useAuth();
  const { includeExcluded, setIncludeExcluded } = useVisibilityOverride();
  const { theme, toggleTheme } = useTheme();

  const roleInfo = ROLE_COLORS[role] || ROLE_COLORS.employee;

  // Check if user is a team leader
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ""}/api/team/status`).then(res => {
      setIsTeamLeader(res.data?.is_team_leader === true);
    }).catch(() => {});
  }, []);

  // Build navigation - ALL roles get My Dashboard + Settings
  const navigation = [];

  if (role === "employee" || role === "user") {
    navigation.push(
      { name: "My Dashboard", href: "/my-dashboard", icon: UserCircle },
    );
    if (isTeamLeader) navigation.push({ name: "My Team", href: "/my-team", icon: Users, badge: "Leader" });
    navigation.push(
      { name: "Company Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "Analytics", href: "/analytics", icon: TrendingUp },
      { name: "Settings", href: "/settings", icon: Settings },
    );
  }

  if (role === "manager") {
    navigation.push(
      { name: "My Dashboard", href: "/my-dashboard", icon: UserCircle },
    );
    if (isTeamLeader) navigation.push({ name: "My Team", href: "/my-team", icon: Users, badge: "Leader" });
    navigation.push(
      { name: "Project Management", href: "/", icon: Users },
      { name: "Company Dashboard", href: "/dashboard", icon: LayoutDashboard },
      { name: "Analytics", href: "/analytics", icon: TrendingUp },
      { name: "Reports", href: "/reports", icon: FileBarChart },
      { name: "Settings", href: "/settings", icon: Settings },
    );
  }

  if (role === "director") {
    navigation.push(
      { name: "My Dashboard", href: "/my-dashboard", icon: UserCircle },
    );
    if (isTeamLeader) navigation.push({ name: "My Team", href: "/my-team", icon: Users, badge: "Leader" });
    navigation.push(
      { name: "Company Dashboard", href: "/", icon: LayoutDashboard },
      { name: "Company P&L", href: "/company", icon: Building2, badge: "Director" },
      { name: "Intelligence", href: "/intelligence", icon: BarChart3, badge: "BI" },
      { name: "Project Management", href: "/team", icon: Users },
      { name: "All Project Management", href: "/all-projects", icon: Briefcase, badge: "Director" },
      { name: "Analytics", href: "/analytics", icon: TrendingUp },
      { name: "Reports", href: "/reports", icon: FileBarChart },
      { name: "Data Source", href: "/data-source", icon: Database },
      { name: "Settings", href: "/settings", icon: Settings },
      { name: "Admin", href: "/admin", icon: Shield, badge: "Director" },
    );
  }

  if (role === "admin") {
    navigation.push(
      { name: "My Dashboard", href: "/my-dashboard", icon: UserCircle },
    );
    if (isTeamLeader) navigation.push({ name: "My Team", href: "/my-team", icon: Users, badge: "Leader" });
    navigation.push(
      { name: "Company Dashboard", href: "/", icon: LayoutDashboard },
      { name: "Project Management", href: "/team", icon: Users },
      { name: "Analytics", href: "/analytics", icon: TrendingUp },
      { name: "Reports", href: "/reports", icon: FileBarChart },
      { name: "Data Source", href: "/data-source", icon: Database },
      { name: "Settings", href: "/settings", icon: Settings },
      { name: "Admin", href: "/admin", icon: Shield, badge: "Admin" },
    );
  }

  const handleLogout = () => { logout(); navigate("/login"); };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/my-dashboard") return "My Dashboard";
    if (path === "/my-team") return "My Team";
    if (path === "/" || path === "/dashboard") return "Company Dashboard";
    if (path === "/team") return "Project Management";
    if (path === "/all-projects") return "All Project Management";
    if (path === "/analytics") return "Analytics";
    if (path === "/data-source") return "Data Source";
    if (path === "/settings") return isAdmin ? "Settings" : "Profile";
    if (path === "/admin") return "Admin Dashboard";
    if (path === "/company") return "Company P&L";
    if (path === "/intelligence") return "Business Intelligence";
    if (path === "/reports") return "Report Builder";
    if (path.startsWith("/consultant/")) return "Consultant Details";
    return "Company Dashboard";
  };

  return (
    <div className="flex min-h-screen">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 border-b border-border bg-background">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center overflow-hidden">
                <img src="/api/logo" alt="SC" className="w-full h-full object-contain" onError={(e) => { e.target.onerror=null; e.target.src="data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 viewBox=%270 0 40 40%27%3E%3Crect width=%2740%27 height=%2740%27 rx=%278%27 fill=%27%233B82F6%27/%3E%3Ctext x=%2720%27 y=%2726%27 text-anchor=%27middle%27 fill=%27white%27 font-size=%2716%27 font-weight=%27bold%27%3ESC%3C/text%3E%3C/svg%3E"; }} />
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground tracking-tight">SpillConsult</h1>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Business Analytics</p>
              </div>
            </Link>
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></Button>
          </div>

          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href || (item.href === "/dashboard" && location.pathname === "/" && (role === "director" || role === "admin"));
              return (
                <Link
                  key={item.name + item.href}
                  to={item.href}
                  className={cn(
                    "flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-[background-color,color,box-shadow] duration-200",
                    isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-4 h-4" />
                    {item.name}
                  </div>
                  {item.badge && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">{item.badge}</Badge>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="px-4 py-3 border-t border-border space-y-3">
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name || "User"}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
              <Badge variant="secondary" className={`mt-2 text-xs ${roleInfo.bg}`}>{roleInfo.label}</Badge>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <div className="flex items-center gap-2 mb-1">
                <RefreshCw className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Data Source</span>
              </div>
              <DataSourceStatus />
            </div>
            <Button variant="outline" className="w-full gap-2" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 h-14 flex items-center justify-between px-6 bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/70 border-b border-border">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-5 h-5" /></Button>
            <h2 className="text-base font-bold text-foreground">{getPageTitle()}</h2>
          </div>
          <div className="flex items-center gap-2">
            {(isAdmin || isDirector || isManager) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full border border-border cursor-pointer" data-testid="visibility-override-toggle" onClick={() => setIncludeExcluded(!includeExcluded)}>
                      {includeExcluded ? <Eye className="w-3.5 h-3.5 text-amber-600" /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
                      <span className={`text-[10px] font-medium ${includeExcluded ? "text-amber-600" : "text-muted-foreground"}`}>
                        {includeExcluded ? "All Data" : "Filtered"}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <p className="text-xs">{includeExcluded ? "Showing ALL data including excluded personnel/projects" : "Visibility defaults applied — hidden data excluded"}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Click to toggle for your session</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleTheme} data-testid="theme-toggle">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-medium text-emerald-700 dark:text-emerald-400">Live</span>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
